import React from 'react';
import { Grid, Divider, withStyles } from '@material-ui/core';

import { v4 as uuidv4 } from 'uuid';
import constants from './constants';
import {
  Action,
  AddTrailer,
  DeleteTrailer,
  DispatchInput,
  Detail,
  Header,
  Notes,
  DefectDetails,
  PaperDeliveryReceiptSection
} from './components';
import {
  getTractorError,
  getDollyError,
  getTrailerError,
  getUpdatedTrailerError,
  getRouteNameDispatchError,
  getEquipmentStatusesVisibleForDispatch,
  getUniqueEquipmentNumber,
  getEquipmentStatusesWithAllowedDispatch,
  findMatchingStraightTractor,
  getDispatchSubmitLabel,
  resolveDispatchSubmitMode,
} from './utils';
import { EQUIPMENT_DEFECTS_GRID_FILTER } from './EquipmentDefects/constants';
import {
  getRouteNameDisplayValue,
  ensureTrailerRouteInOptions,
  resolveBaseRouteOptions,
  mergeUserCreatedRoutes,
  shouldShowAddRouteOption,
  resolveRouteOnTrailerChange,
  getRouteOptionsForDuplicateCheck,
  getRouteOptionsIncludingFromAllTrailers,
  resolveSelectedMappingRouteName,
  isEquipmentRouteMatchingSelectedRoute,
} from './routeNameUtils';
import { ALPHA_NUMERIC_REGEX } from '../constRegex';
import { isEqual } from 'lodash/lang';
import compose from 'recompose/compose';
import { withAppConfig } from '../../../../../appConfigContext';
import PreDispatchIdealStartTimeEditor from './PreDispatchIdealStartTimeEditor';
import { applyStraightTruckTrailerAtIndexForPreDispatch } from './preDispatchTrailerUtils';
import {
  getCheckboxState,
  hasAnyPhysicalDrNeed,
  isTrailerSelected as isTrailerSelectedUtil
} from '../PrintPhysicalDr/utils/dispatchPanelUtils';

const styles = (theme) => ({
  container: {
    flexGrow: 1,
    overflowY: 'auto'
  },
  detailContainer: {
    padding: '0px 20px'
  },
  divider: {
    backgroundColor: theme.gridIconBorderColor,
    opacity: 0.6
  },
  deleteTrailer: {
    padding: '10px'
  }
});

class DispatchTrailer extends React.Component {
  constructor(props) {
    super(props);
    this.generateTrailerList();
    this.userCreatedRoutesByTrailer = {};
    const initialTrailerDetails = props.initialTrailerDetails ?? [this.getNewTrailer()];
    const initialTractor = props.initialTractor ?? null;
    this.state = {
      trailerDetails: initialTrailerDetails,
      trailerDetailsErrors: [null],
      tractor: initialTractor,
      notes: '',
      tractorError: null,
      isLoading: props.isLoading ?? true,
      idealStartTimes: [],
      printDrAfterDispatch: false
    };
    this.isUpdatingTractor = false;
  }

  isRouteNameRequiredForDispatch = () =>
    Boolean(this.props.configJSON?.routeNameRequiredForDispatch);

  // Optional chaining keeps this callable from the constructor, before state exists.
  getSubmitIsPreDispatch = (tractor = this.state?.tractor, trailerDetails = this.state?.trailerDetails) =>
    resolveDispatchSubmitMode({
      isPreDispatchPanel: Boolean(this.props.isPreDispatch),
      isDriverEligibleForNormalDispatch: Boolean(this.props.isDriverEligibleForNormalDispatch),
      tractor,
      trailerDetails,
    });

  /** The /TBF default is only a valid starting value when the request submits as Pre-Dispatch. */
  getDefaultDolly = (tractor, trailerDetails) =>
    this.getSubmitIsPreDispatch(tractor, trailerDetails) ? this.props.defaultDolly ?? null : null;

  generateTrailerList = () => {
    this.trailerListWithoutStraightTruck = this.props.trailerList.filter(
      (trailer) => !trailer.isStraightTruck
    );
  };

  componentDidMount() {
    this.props.onTrailerDetailsChange?.(this.state.trailerDetails);
  }

  componentWillUnmount() {
    this.trailerListWithoutStraightTruck = null;
    this.userCreatedRoutesByTrailer = null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.trailerDetails.length !== this.state.trailerDetails.length) {
      this.props.onTrailerDetailsChange?.(this.state.trailerDetails);
    }
    if (prevState.trailerDetails !== this.state.trailerDetails) {
      this.resetPrintDrIfCheckboxDisabled();
    }
    const equipmentChanged =
      prevProps.allTrailers !== this.props.allTrailers ||
      (this.props.isPreDispatch &&
        (prevProps.tractorList !== this.props.tractorList ||
          prevProps.dollyList !== this.props.dollyList));
    if (!equipmentChanged) return;

    this.generateTrailerList();
    if (this.state.isLoading && !this.props.isLoading) {
      this.setState({ isLoading: this.props.isLoading }, () => {
        this.processPendingRouteSelections();
        if (this.props.getSuggestedTractorOnLoad) {
          const tractor = this.props.getSuggestedTractorOnLoad();
          if (tractor && this.state.tractor?.value !== tractor?.value) {
            this.isUpdatingTractor = true;
            this.setState(
              {
                tractor,
                tractorError: getTractorError({
                  tractor,
                  trailerNumber: this.state.trailerDetails[0]?.trailerNumber,
                  tractorStatusConfig: this.props.tractorStatusConfig,
                  isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails),
                  AllowAnyStraightTruckInDriverDispatch:
                    this.props.configJSON.allowAnyStraightTruckInDriverDispatch,
                  isPreDispatch: this.getSubmitIsPreDispatch(tractor)
                })
              },
              () => {
                this.isUpdatingTractor = false;
              }
            );
          }
        }
        if (this.props.getSuggestedDollyOnLoad) {
          this.setState(
            (state) => {
              const updated = this.props.getSuggestedDollyOnLoad(
                state.trailerDetails,
                this.getSubmitIsPreDispatch(state.tractor, state.trailerDetails)
              );
              return updated ? { trailerDetails: updated } : null;
            },
            () => {
              this.state.trailerDetails.forEach((trailer, index) => {
                if (index > 0 && trailer.dolly) this.updateDollyError(index);
              });
            }
          );
        }
      });
    } else if (!this.state.isLoading) {
      if (this.props.getSuggestedDollyOnLoad) {
        this.setState(
          (state) => {
            const updated = this.props.getSuggestedDollyOnLoad(
              state.trailerDetails,
              this.getSubmitIsPreDispatch(state.tractor, state.trailerDetails)
            );
            return updated ? { trailerDetails: updated } : null;
          },
          () => this.updateState()
        );
        return;
      }
      this.updateState();
    }
  }

  processPendingRouteSelections = () => {
    this.state.trailerDetails.forEach((trailer, index) => {
      if (trailer.routeName) {
        this.updateRouteName(index, true)({ value: trailer.routeName });
      }
    });
  };

  setTractorSafely = (tractor, callback) => {
    if (this.state.isLoading || this.state.tractor?.value === tractor?.value) {
      if (callback) callback();
      return false;
    }
    const previousSubmitIsPreDispatch = this.getSubmitIsPreDispatch();
    const submitIsPreDispatch = this.getSubmitIsPreDispatch(tractor);
    this.isUpdatingTractor = true;
    this.setState(
      {
        tractor,
        tractorError: getTractorError({
          tractor,
          trailerNumber: this.state.trailerDetails[0]?.trailerNumber,
          tractorStatusConfig: this.props.tractorStatusConfig,
          isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails),
          AllowAnyStraightTruckInDriverDispatch:
            this.props.configJSON.allowAnyStraightTruckInDriverDispatch,
          isPreDispatch: submitIsPreDispatch
        })
      },
      () => {
        this.isUpdatingTractor = false;
        if (previousSubmitIsPreDispatch !== submitIsPreDispatch) {
          this.refreshTrailerError();
        }
        if (callback) callback();
      }
    );
    return true;
  };

  updateState = () => {
    if (this.props.isPreDispatch && this.isUpdatingTractor) return;
    if (this.props.computeUpdateState) {
      this.setState(
        (state) =>
          this.props.computeUpdateState(
            state,
            this.getSubmitIsPreDispatch(state.tractor, state.trailerDetails)
          ),
        this.refreshErrors
      );
      return;
    }
    this.setState(
      (state) => ({
        trailerDetails: state.trailerDetails.map((eachTrailer) => {
          const trailer =
            this.props.allTrailers.find(
              (equipment) => equipment.trailerId === eachTrailer?.trailerId
            ) ?? this.getNewTrailer();
          const dolly =
            this.props.dollyList.find((d) => d.value === eachTrailer.dolly?.value) ?? null;
          return { ...trailer, dolly };
        }),
        tractor: this.props.tractorList.find((t) => t.value === state.tractor?.value) ?? null
      }),
      this.refreshErrors
    );
  };

  refreshErrors = () => {
    if (this.state.isLoading) return;
    this.refreshTrailerError();
    if (this.state.tractor) {
      const submitIsPreDispatch = this.getSubmitIsPreDispatch();
      this.setState((state) => ({
        tractorError: getTractorError({
          tractor: state.tractor,
          trailerNumber: this.state.trailerDetails[0]?.trailerNumber,
          tractorStatusConfig: this.props.tractorStatusConfig,
          isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails),
          AllowAnyStraightTruckInDriverDispatch:
            this.props.configJSON.allowAnyStraightTruckInDriverDispatch,
          isPreDispatch: submitIsPreDispatch
        })
      }));
    }
  };

  getTrailerDetails = () => {
    if (
      this.props.configJSON.allowAnyStraightTruckInDriverDispatch &&
      this.state.trailerDetails[0]?.isStraightTruck
    ) {
      return this.state.trailerDetails[0];
    }
    return this.getNewTrailer();
  };

  getNewTrailer = () => {
    if (this.props.isPreDispatch && this.state?.trailerDetails?.length === 0) {
      const trailer = this.props.getNewTrailerFromSelection?.();
      if (trailer) return trailer;
    }
    return {
      id: uuidv4(),
      trailerNumber: null,
      manifestNumber: null,
      routeName: null,
      dolly: this.getDefaultDolly()
    };
  };

  updateNotes = (notes) => {
    this.setState({
      notes
    });
  };

  handlePrintCheckboxChange = (checked) => {
    this.setState({
      printDrAfterDispatch: checked
    });
  };

  resetPrintDrIfCheckboxDisabled = () => {
    if (!this.state.printDrAfterDispatch) return;
    const { isDisabled } = getCheckboxState({
      isTrailerSelected: isTrailerSelectedUtil(this.state.trailerDetails),
      isPhysicalDrNeed: hasAnyPhysicalDrNeed(this.state.trailerDetails)
    });
    if (isDisabled) {
      this.setState({ printDrAfterDispatch: false });
    }
  };

  // Straight-truck selection: dispatch vs pre-dispatch
  handleStraightTruckReplacement = ({ updateIndex, trailerData, onTrailerReplaced }) => {
    const matchingTractor = findMatchingStraightTractor(trailerData, this.props.tractorList);
    if (!this.props.isPreDispatch) {
      this.handleStraightTruckReplacementLegacy({
        updateIndex,
        trailerData,
        matchingTractor,
        onTrailerReplaced
      });
      return;
    }
    this.handleStraightTruckReplacementPreDispatch(updateIndex, trailerData, matchingTractor);
  };

  /** Original non–pre-dispatch straight-truck behavior: persist trailer (incl. routeName), then sync tractor. */
  handleStraightTruckReplacementLegacy = ({
    updateIndex,
    trailerData,
    matchingTractor,
    onTrailerReplaced
  }) => {
    this.setState(
      (state) => ({
        trailerDetails: state.trailerDetails.map((trailer, index) =>
          index === updateIndex ? trailerData : trailer
        )
      }),
      () => {
        this.updateTrailerError(updateIndex);
        // Prefer updateTractor when we already have trailerData so onChangeTractor does not
        // re-derive the trailer from allTrailers (which can drop routeName for closed/visible-only statuses).
        if (matchingTractor) {
          this.updateTractor(matchingTractor);
        } else {
          this.onChangeTractor(matchingTractor);
        }
        if (onTrailerReplaced) onTrailerReplaced();
      }
    );
  };

  /** Pre-dispatch: persist ST row, refresh all row errors (ST-after-non-ST rule), then tractor. */
  handleStraightTruckReplacementPreDispatch = (updateIndex, trailerData, matchingTractor) => {
    const trailerDetails = applyStraightTruckTrailerAtIndexForPreDispatch(
      this.state.trailerDetails,
      updateIndex,
      trailerData,
      this.getDefaultDolly()
    );
    // Only a request already submitting as Pre-Dispatch falls back to the /TBF suggestion.
    const fallbackTractor = this.getSubmitIsPreDispatch() ? this.props.initialTractor : null;
    if (this.props.configJSON.allowAnyStraightTruckInDriverDispatch && !matchingTractor) {
      this.setState({ trailerDetails }, () => {
        this.refreshTrailerError();
        this.onChangeTractor(matchingTractor || fallbackTractor);
      });
      return;
    }
    this.updateTrailerDetails({
      trailerDetails,
      tractor: matchingTractor || fallbackTractor,
    });
  };

  // function to handle regular trailer replacement
  handleRegularTrailerReplacement = ({ updateIndex, trailerData, onTrailerReplaced }) => {
    // Pre-Dispatch re-seeds the /TBF default on the replaced row; Dispatch keeps the selected dolly.
    const submitIsPreDispatch = this.getSubmitIsPreDispatch();
    const trailerDetails = this.state.trailerDetails.map((trailer, index) => {
      if (index !== updateIndex) {
        return trailer;
      }
      const dolly = submitIsPreDispatch ? this.getDefaultDolly() : (trailer.dolly ?? null);
      return { ...trailerData, dolly };
    });
    // Clear tractor if it was a straight truck and trailer changed
    const tractor = this.state.tractor?.isStraightTruck ? null : this.state.tractor;
    this.updateTrailerDetails({ trailerDetails, tractor, onTrailerReplaced });
  };

  replaceTrailer = ({ updateIndex, data, onTrailerReplaced }) => {
    const trailerData = data || this.getNewTrailer();
    // Handle straight truck trailers differently
    if (trailerData?.isStraightTruck) {
      this.handleStraightTruckReplacement({ updateIndex, trailerData, onTrailerReplaced });
    } else {
      this.handleRegularTrailerReplacement({ updateIndex, trailerData, onTrailerReplaced });
    }
  };

  getValidTrailersByRoute = (routeName) => {
    const { allTrailers, trailerStatusConfig, routeList = [] } = this.props;
    const trailerStatusInOrder = getEquipmentStatusesVisibleForDispatch(trailerStatusConfig);
    if (!routeName) return [];
    return (
      allTrailers.filter(
        (trailer) =>
          trailerStatusInOrder.includes(trailer.trailerStatus) &&
          isEquipmentRouteMatchingSelectedRoute({
            equipmentRouteName: trailer.routeName,
            selectedRouteName: routeName,
            routeList,
          })
      ) ?? []
    );
  };

  // Merges API trailer data with current state; for closed trailers re-selected via spurious Select update, preserves user's routeName over API value.
  getMergedTrailerData = (trailerData, currentTrailer) => {
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    const isSameTrailer =
      currentTrailer?.trailerId?.toLowerCase() === trailerData?.trailerId?.toLowerCase();
    const isClosed = enableNewRoute && this.isClosedTrailer(trailerData?.trailerStatus);
    const merged =
      isClosed && isSameTrailer
        ? { ...trailerData, routeName: currentTrailer?.routeName }
        : trailerData;
    return { merged, hasChanged: !isEqual(merged, currentTrailer) };
  };

  updateTrailerNumber = (updateIndex) => (value) => {
    const trailerId = value?.value;
    const trailerData = this.props.allTrailers.find(
      (equipment) => equipment.trailerId?.toLowerCase() === trailerId?.toLowerCase()
    );
    const currentTrailer = this.state.trailerDetails[updateIndex];
    const { merged, hasChanged } = this.getMergedTrailerData(trailerData, currentTrailer);
    //dont replace trailer if same trailer selected again
    if (hasChanged) {
      const resolvedRouteName = resolveRouteOnTrailerChange({
        currentTrailer,
        newTrailerData: merged,
        userCreatedRoutesByTrailer: this.userCreatedRoutesByTrailer,
        enableNewRouteAssignmentForTrailer: Boolean(
          this.props.configJSON?.enableNewRouteAssignmentForTrailer
        ),
        routeList: this.props.routeList || []
      });
      if (!isEqual(trailerData, this.state.trailerDetails[updateIndex])) {
        this.replaceTrailer({
          updateIndex,
          data: { ...merged, routeName: resolvedRouteName }
        });
      }
    }
  };

  isEmptyTrailer = (trailerStatus, routeId) => {
    const EMPTY_TRAILER_STATUS = this.props.trailerStatusConfig.find(
      (trailerCong) => trailerCong.rmaxCode === constants.RMAX_EMPTY_TRAILER_CODE
    )?.status;
    return trailerStatus === EMPTY_TRAILER_STATUS && !routeId;
  };

  isClosedTrailer = (trailerStatus) => {
    const CLOSED_TRAILER_STATUS = this.props.trailerStatusConfig?.find(
      (c) => c.rmaxCode === constants.RMAX_CLOSED_TRAILER_CODE
    )?.status;
    return trailerStatus === CLOSED_TRAILER_STATUS;
  };

  // When user picks a route, we either (a) fill the slot with a trailer that has that route, or (b) just set the route on the trailer already in the slot.
  // (a) Pre-select: slot has no trailer or is an empty trailer → replace slot with the first trailer that has the selected route.
  // (b) No pre-select: slot already has a trailer (e.g. 931282UPGF with DEFAULTROUTE) → keep that trailer and only update its route (do not swap to another trailer that has the route).
  shouldPreSelectTrailerForRoute = (selectedTrailer, preSelectRouteTrailers) => {
    return (
      preSelectRouteTrailers?.length > 0 &&
      (this.isEmptyTrailer(selectedTrailer.trailerStatus, selectedTrailer.routeId) ||
        !selectedTrailer?.trailerId)
    );
  };

  storeRouteNameDuringLoading = (updateIndex, routeName) => {
    this.setState((state) => ({
      trailerDetails: state.trailerDetails.map((trailer, index) =>
        index === updateIndex ? { ...trailer, routeName } : trailer
      )
    }));
  };

  // Stores user-created (Creatable) route names keyed by trailerId so they persist after switching routes and follow the trailer across slots.
  trackUserCreatedRoute = (trailerId, selection, routeName) => {
    if (selection?.isNewValue && routeName && trailerId) {
      const existing = this.userCreatedRoutesByTrailer[trailerId] || [];
      if (!existing.some((r) => r.value === routeName)) {
        this.userCreatedRoutesByTrailer[trailerId] = [
          ...existing,
          { label: routeName, value: routeName }
        ];
      }
    }
  };

  // Returns user-created routes for a trailer (by trailerId); empty when feature flag is off or no trailerId.
  getUserCreatedRoutes = (trailerId) => {
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    return enableNewRoute && trailerId ? this.userCreatedRoutesByTrailer?.[trailerId] || [] : [];
  };

  // Returns null for user-created routes (handled by mergeUserCreatedRoutes) so ensureTrailerRouteInOptions does not treat them as equipment routes
  getTrailerRouteExcludingUserCreated = (trailer, userCreatedRoutes) => {
    const isUserCreated = userCreatedRoutes.some((r) => r.value === trailer?.routeName);
    return isUserCreated ? null : trailer?.routeName;
  };

  updateRouteName = (updateIndex, isPendingRouteSelection = false) => (value) => {
    const routeName = resolveSelectedMappingRouteName({
      selection: value,
      routeList: this.props.routeList || [],
      maxLength: this.props.routeNameConfig?.maxLength,
    });
    const selectedTrailer = this.state.trailerDetails[updateIndex];
    const disableRouteTrailerMapping = this.props.getRouteNameDisableEdit?.(selectedTrailer, updateIndex);

      this.trackUserCreatedRoute(selectedTrailer?.trailerId, value, routeName);

      // If loading is true, just store the route name in trailer state
      if (this.state.isLoading) {
        this.storeRouteNameDuringLoading(updateIndex, routeName);
        return;
      }

    let preSelectRouteTrailers = [];
    const selectedTrailerRoute = selectedTrailer?.routeName;
    if (!this.isEmptyTrailer(selectedTrailer.trailerStatus, selectedTrailer.routeId)) { //pre select trailer for a selected route only if non empty trailers selected
      preSelectRouteTrailers = this.getValidTrailersByRoute(routeName);
    }
    const selectRouteTrailerData = this.getValidTrailersByRoute(selectedTrailerRoute).map(
      (trailer) => trailer.trailerId
    );
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    // For closed trailer: clearing route must not clear trailer selection (only when feature flag is on)
    const isClosed = enableNewRoute && this.isClosedTrailer(selectedTrailer?.trailerStatus);
    const shouldClearTrailer =
      !isClosed &&
      routeName === null &&
      selectRouteTrailerData.length > 0 &&
      !disableRouteTrailerMapping &&
      selectRouteTrailerData.includes(selectedTrailer?.trailerId);
    // When flag off: old behavior — replace trailer with one that has the selected route. When flag on: only replace when slot is empty or has no trailer (keep trailer e.g. DEFAULTROUTE case).
    const shouldPreSelectTrailer = enableNewRoute
      ? this.shouldPreSelectTrailerForRoute(selectedTrailer, preSelectRouteTrailers)
      : (preSelectRouteTrailers?.length > 0);
    // Pre-dispatch only: multiple trailers can share a route; do not swap this row to preSelectRouteTrailers[0] when it already shows a trailer on the selected route.
    const rowTrailerAlreadyOnSelectedRoute = Boolean(
      selectedTrailer?.trailerId &&
      preSelectRouteTrailers.some(
        (t) =>
          t.trailerId?.toLowerCase() === selectedTrailer.trailerId?.toLowerCase()
      )
    );
    const shouldApplyPreSelectedTrailer =
      shouldPreSelectTrailer &&
      !disableRouteTrailerMapping &&
      !(this.props.isPreDispatch && rowTrailerAlreadyOnSelectedRoute);
    const trailerData = shouldClearTrailer
        ? null
        : {
            ...selectedTrailer,
            ...(shouldApplyPreSelectedTrailer && { ...preSelectRouteTrailers[0] }),
            routeName
          };
      //dont replace trailer if same trailer selected again

      const onTrailerReplaced = () => {
        if (!this.state.isLoading) {
          this.updateRouteNameError(updateIndex);
        }
      };

      if (!isEqual(trailerData, selectedTrailer) || isPendingRouteSelection) {
        this.replaceTrailer({ updateIndex, data: trailerData, onTrailerReplaced });
      } else {
        this.updateRouteNameError(updateIndex);
      }
    };

  updateRouteNameError = (updateIndex) => {
    if (this.state.isLoading) return;
    const routeNameRequiredForDispatch = this.isRouteNameRequiredForDispatch();
    this.setState((state) => ({
      trailerDetailsErrors: state.trailerDetailsErrors.map((error, index) => {
        if (index !== updateIndex) return error;
        const trailer = state.trailerDetails[index];
        return {
          ...(error || {}),
          routeName: getRouteNameDispatchError({
            trailer,
            routeNameRequiredForDispatch
          })
        };
      })
    }));
  };

  updateDolly = (updateIndex) => (dolly) => {
    const previousSubmitIsPreDispatch = this.getSubmitIsPreDispatch();
    const nextTrailerDetails = this.state.trailerDetails.map((trailer, index) =>
      index === updateIndex
        ? {
            ...trailer,
            dolly
          }
        : trailer
    );
    const submitIsPreDispatch = this.getSubmitIsPreDispatch(
      this.state.tractor,
      nextTrailerDetails
    );
    this.setState({ trailerDetails: nextTrailerDetails }, () => {
      if (previousSubmitIsPreDispatch !== submitIsPreDispatch) {
        this.refreshErrors();
        return;
      }
      this.updateDollyError(updateIndex);
    });
  };

  updateDollyError = (updateIndex) => {
    const submitIsPreDispatch = this.getSubmitIsPreDispatch();
    this.setState((state) => ({
      trailerDetailsErrors: state.trailerDetailsErrors.map((error, index) => {
        if (index === updateIndex) {
          const trailer = state.trailerDetails[index];
          const dollyError = getDollyError({
            dolly: trailer.dolly,
            trailersData: state.trailerDetails,
            dollyStatusConfig: this.props.dollyStatusConfig,
            isPreDispatch: submitIsPreDispatch
          });
          return { ...error, dolly: dollyError };
        }
        if (error?.dolly === constants.ERROR_MESSAGES.DUPLICATE_DOLLY) {
          const trailer = state.trailerDetails[index];
          return {
            ...error,
            dolly: getDollyError({
              dolly: trailer.dolly,
              trailersData: state.trailerDetails,
              dollyStatusConfig: this.props.dollyStatusConfig,
              isPreDispatch: submitIsPreDispatch
            })
          };
        }
        return error;
      })
    }));
  };

  updateTrailerError = (updateIndex) => {
    if (this.state.isLoading) return;
    const { trailerStatusConfig } = this.props;
    const isPreDispatch = this.props.isPreDispatch;
    const routeNameRequiredForDispatch = this.isRouteNameRequiredForDispatch();
    this.setState((state) => ({
      trailerDetailsErrors: state.trailerDetails[0]?.isStraightTruck
        ? [
            {
              trailerNumber: getTrailerError({
                trailer: state.trailerDetails[0],
                trailersData: state.trailerDetails,
                trailerStatusConfig,
                trailerIndex: 0,
                isPreDispatch: !!isPreDispatch
              }),
              routeName: getRouteNameDispatchError({
                trailer: state.trailerDetails[0],
                routeNameRequiredForDispatch
              })
            }
          ]
        : state.trailerDetailsErrors.map((error, index) => {
            if (index === updateIndex) {
              const trailer = state.trailerDetails[index];
              return {
                ...error,
                trailerNumber: getTrailerError({
                  trailer,
                  trailersData: state.trailerDetails,
                  trailerStatusConfig,
                  trailerIndex: index,
                  isPreDispatch: !!isPreDispatch
                }),
                routeName: getRouteNameDispatchError({
                  trailer,
                  routeNameRequiredForDispatch
                })
              };
            }
            if (error?.trailerNumber === constants.ERROR_MESSAGES.DUPLICATE_TRAILER) {
              const trailer = state.trailerDetails[index];
              return {
                ...error,
                trailerNumber: getTrailerError({
                  trailer,
                  trailersData: state.trailerDetails,
                  trailerStatusConfig,
                  trailerIndex: index,
                  isPreDispatch: !!isPreDispatch
                }),
                routeName: getRouteNameDispatchError({
                  trailer,
                  routeNameRequiredForDispatch
                })
              };
            }
            return error;
          })
    }));
  };

  onChangeTractor = (tractor) => {
    const index = 0;
    const tractorDetails = this.props.tractorList.find(
      (equipment) => equipment.value?.toLowerCase() === tractor?.value?.toLowerCase()
    );
    if (
      tractorDetails?.isStraightTruck &&
      this.state.tractor?.value?.toLowerCase() !== tractorDetails?.value?.toLowerCase()
    ) {
      const uniqueTractorNumber = getUniqueEquipmentNumber(
        tractorDetails.label,
        tractorDetails?.tractorScac
      );
      const TRAILER_VALID_STATUS = getEquipmentStatusesWithAllowedDispatch(
        this.props.trailerStatusConfig
      );
      const selectedTrailer = this.props.allTrailers
        .filter((trailer) => TRAILER_VALID_STATUS.includes(trailer?.trailerStatus))
        .find((equipment) => {
          const uniqueTrailerNumber = getUniqueEquipmentNumber(
            equipment.trailerNumber,
            equipment?.trailerScac
          );
          return uniqueTractorNumber === uniqueTrailerNumber;
        });
      const isStraightTrailer = selectedTrailer?.isStraightTruck;
      const trailerDetails = isStraightTrailer ? selectedTrailer : this.getTrailerDetails();
      // check if trailer is a straight trailer
      // if straight than update the trailer with matching tractor otherwise clear the dropdown of trailer
      if (
        !this.props.isPreDispatch &&
        this.state.trailerDetails[index]?.trailerNumber !== trailerDetails?.trailerNumber
      ) {
        this.updateTrailerDetails({
          trailerDetails: [trailerDetails],
          tractor: tractorDetails
        });
        return;
      }
      this.updateTractor(tractorDetails);
    } else if (
      this.props.configJSON.allowAnyStraightTruckInDriverDispatch &&
      this.isStraightTruckSelected(tractor, index)
    ) {
      return;
    } else if (!tractorDetails || !tractorDetails?.isStraightTruck) {
      // check if new tractor selected is not straight but previously selected tractor was straight truck , then we need to clear the previous selected straight trailer also
      if (!this.props.isPreDispatch && this.state.tractor?.isStraightTruck) {
        this.updateTrailerDetails({
          trailerDetails: [this.getTrailerDetails()],
          tractor: tractorDetails
        });
        return;
      }
      this.updateTractor(tractorDetails);
    }
  };

  isStraightTruckSelected = (tractor, index) => {
    // if flag is enabled
    // and tractor is not selected/not matching
    // and trailer is straight truck
    // and already selected tractor also a straight truck then do nothing
    return (
      !tractor &&
      this.state.trailerDetails[index]?.isStraightTruck &&
      this.state.tractor?.isStraightTruck
    );
  };

  updateTrailerDetails = ({ trailerDetails, tractor, onTrailerReplaced }) => {
    // Read before the rows change: a cleared tractor only returns to /TBF when the request was submitting as Pre-Dispatch.
    const wasSubmitPreDispatch = this.getSubmitIsPreDispatch();
    if (this.props.isPreDispatch) this.isUpdatingTractor = true;
    this.setState({ trailerDetails }, () => {
      // Recompute all trailer-row errors so cross-row straight-truck constraints stay in sync.
      if (this.props.isPreDispatch && !trailerDetails[0]?.isStraightTruck) {
        this.refreshTrailerError();
      } else {
        this.updateTrailerError(0);
      }
      this.updateTrailerError(0);
      const invokeOnTrailerReplaced = () => {
        if (onTrailerReplaced) onTrailerReplaced();
      };
      if (!tractor || !this.props.isPreDispatch) {
        if (tractor) {
          this.updateTractor(tractor);
        } else{
          this.updateTractor(wasSubmitPreDispatch ? this.props.initialTractor : null);
        }
        if (this.props.isPreDispatch) this.isUpdatingTractor = false;
        invokeOnTrailerReplaced();
      } else if (!this.setTractorSafely(tractor, invokeOnTrailerReplaced)) {
        this.isUpdatingTractor = false;
        invokeOnTrailerReplaced();
      }
    });
  };

  updateTractor = (tractor) => {
    if (this.props.isPreDispatch) {
      this.setTractorSafely(tractor);
      return;
    }
    if (this.state.isLoading) return;
    const previousSubmitIsPreDispatch = this.getSubmitIsPreDispatch();
    const submitIsPreDispatch = this.getSubmitIsPreDispatch(tractor);
    this.setState(
      {
        tractor,
        tractorError: getTractorError({
          tractor,
          trailerNumber: this.state.trailerDetails[0]?.trailerNumber,
          tractorStatusConfig: this.props.tractorStatusConfig,
          isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails),
          AllowAnyStraightTruckInDriverDispatch:
            this.props.configJSON.allowAnyStraightTruckInDriverDispatch,
          isPreDispatch: submitIsPreDispatch
        })
      },
      () => {
        if (previousSubmitIsPreDispatch !== submitIsPreDispatch) {
          this.refreshTrailerError();
        }
      }
    );
  };

  addNewTrailer = () => {
    const newTrailerData = this.getNewTrailer();
    const previousErrorRuleState = this.getErrorRuleState();
    this.setState(
      (state) => ({
        trailerDetails: [...state.trailerDetails, newTrailerData],
        trailerDetailsErrors: [...state.trailerDetailsErrors, null]
      }),
      () => this.refreshErrorsIfRulesChanged(previousErrorRuleState)
    );
  };

  /** Submit mode and straight-truck shape decide which tractor/dolly rules apply, so stored errors go stale when either changes. */
  getErrorRuleState = () => ({
    submitIsPreDispatch: this.getSubmitIsPreDispatch(),
    isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails)
  });

  /** Returns true when the rules changed and every stored error was recomputed. */
  refreshErrorsIfRulesChanged = (previousErrorRuleState) => {
    const current = this.getErrorRuleState();
    if (
      current.submitIsPreDispatch === previousErrorRuleState.submitIsPreDispatch &&
      current.isStraightTruckTrailer === previousErrorRuleState.isStraightTruckTrailer
    ) {
      return false;
    }
    this.refreshErrors();
    return true;
  };

  refreshTrailerError = () => {
    if (this.state.isLoading) return;
    const isPreDispatch = this.props.isPreDispatch;
    const submitIsPreDispatch = this.getSubmitIsPreDispatch();
    this.setState({
      trailerDetailsErrors: getUpdatedTrailerError({
        trailerDetails: this.state.trailerDetails,
        trailerDetailsErrors: this.state.trailerDetailsErrors,
        trailerStatusConfig: this.props.trailerStatusConfig,
        dollyStatusConfig: this.props.dollyStatusConfig,
        routeNameRequiredForDispatch: this.isRouteNameRequiredForDispatch(),
        isSubmitPreDispatch: submitIsPreDispatch,
        ...(isPreDispatch && { isPreDispatch: true })
      })
    });
  };

  removeTrailer = (removeIndex) => {
    const previousErrorRuleState = this.getErrorRuleState();
    this.setState(
      (state) => ({
        trailerDetails: state.trailerDetails.filter((_trailer, index) => index !== removeIndex),
        trailerDetailsErrors: state.trailerDetailsErrors.filter(
          (_error, index) => index !== removeIndex
        )
      }),
      () => {
        if (!this.refreshErrorsIfRulesChanged(previousErrorRuleState)) {
          this.refreshTrailerError();
        }
      }
    );
  };

  getTrailerDetailsError = (trailer, index) => {
    const isPreDispatch = this.props.isPreDispatch;
    const submitIsPreDispatch = this.getSubmitIsPreDispatch();
    const trailerError = getTrailerError({
      trailer,
      trailersData: this.state.trailerDetails,
      trailerStatusConfig: this.props.trailerStatusConfig,
      trailerIndex: index,
      isPreDispatch: !!isPreDispatch
    });
    const dollyError = index
      ? getDollyError({
          dolly: trailer.dolly,
          trailersData: this.state.trailerDetails,
          dollyStatusConfig: this.props.dollyStatusConfig,
          isPreDispatch: submitIsPreDispatch
        })
      : null;
    const routeNameError = getRouteNameDispatchError({
      trailer,
      routeNameRequiredForDispatch: this.isRouteNameRequiredForDispatch()
    });
    if (trailerError || dollyError || routeNameError) {
      return {
        trailerNumber: trailerError,
        dolly: dollyError,
        routeName: routeNameError
      };
    }
    return null;
  };

  isValidTrailerDispatch = () => {
    const trailerDetailsErrors = [];
    this.state.trailerDetails.forEach((trailerDetail, index) =>
      trailerDetailsErrors.push(this.getTrailerDetailsError(trailerDetail, index))
    );
    const submitIsPreDispatch = this.getSubmitIsPreDispatch();
    const tractorError = getTractorError({
      tractor: this.state.tractor,
      trailerNumber: this.state.trailerDetails[0]?.trailerNumber,
      tractorStatusConfig: this.props.tractorStatusConfig,
      isStraightTruckTrailer: this.checkIfStraightTruck(this.state.trailerDetails),
      AllowAnyStraightTruckInDriverDispatch:
        this.props.configJSON.allowAnyStraightTruckInDriverDispatch,
      isPreDispatch: submitIsPreDispatch
    });
    this.setState({ trailerDetailsErrors, tractorError });
    return !Boolean(tractorError || trailerDetailsErrors.filter(Boolean).length);
  };

  checkIfStraightTruck = (trailerDetails) => {
    return trailerDetails.length === 1 && trailerDetails[0].isStraightTruck;
  };

  /** Prop (user edit) first, then trailer ideal/planned — shared by dispatch payload and Required validation */
  getEffectiveIdealStartTimeValue = (trailerIndex) => {
    const trailer = this.state.trailerDetails[trailerIndex];
    if (!trailer) return null;
    return (
      this.props.preDispatchIdealStartTimes?.[trailerIndex] ||
      trailer.idealStartTime ||
      trailer.plannedRouteStartTime ||
      null
    );
  };

  /** True when dispatcher entered start time for a trailer without planning time. */
  getIsManualStartTime = (trailerIndex) => {
    const trailer = this.state.trailerDetails[trailerIndex];
    if (!this.getSubmitIsPreDispatch() || !trailer || trailer.isAlreadyDispatchedRoute) {
      return false;
    }
    if (!this.props.isStartTimeEditable?.(trailer)) {
      return false;
    }
    return Boolean(this.getEffectiveIdealStartTimeValue(trailerIndex));
  };

  isIdealStartTimeEditable = (trailer) =>
    Boolean(
      this.getSubmitIsPreDispatch() &&
        this.props.isStartTimeEditable?.(trailer) &&
        !trailer?.isAlreadyDispatchedRoute
    );

  dispatchTrailer = () => {
    const submitIsPreDispatch = this.getSubmitIsPreDispatch();
    let data = this.isValidTrailerDispatch()
      ? {
          tractorId: this.state.tractor?.value,
          notes: this.state.notes,
          isPreDispatch: submitIsPreDispatch,
          trailerDetails: this.state.trailerDetails.map((trailer, trailerIndex) => ({
            trailerId: trailer.trailerId ?? trailer.id ?? null,
            manifestNumber: trailer.manifestNumber || null,
            dollyId: trailer.dolly?.value || null,
            routeName: trailer.routeName || null,
            ...(submitIsPreDispatch && {
              isAlreadyDispatchedRoute: trailer.isAlreadyDispatchedRoute || false,
              plannedRouteStartTime: this.getEffectiveIdealStartTimeValue(trailerIndex),
              isManualStartTime: this.getIsManualStartTime(trailerIndex)
            })
          })),
          printDrAfterDispatch: this.state.printDrAfterDispatch
        }
      : null;
    if (data && this.props.normalizeDispatchPayload) {
      data = this.props.normalizeDispatchPayload(data);
    }
    this.props.dispatchDriver(data);
  };

  renderDivider = (index) =>
    index <= this.state.trailerDetails.length - 1 ? (
      <Divider className={this.props.classes.divider} />
    ) : null;

  renderDeleteControl = (trailerIndex, isRouteNameEditDisabled) => {
    if (trailerIndex === 0) return null;
    if (isRouteNameEditDisabled) {
      return <div className={this.props.classes.deleteTrailer} />;
    }
    return <DeleteTrailer id={trailerIndex} onClick={this.removeTrailer} />;
  };
  // Returns trailerIds whose user-created routes include the given routeName.
  getTrailerIdsForUserCreatedRoute = (routeName) => {
    if (!routeName || !this.userCreatedRoutesByTrailer) return [];
    return Object.keys(this.userCreatedRoutesByTrailer).filter((trailerId) =>
      (this.userCreatedRoutesByTrailer[trailerId] || []).some((r) => r.value === routeName)
    );
  };

  getFilteredTrailerOptions = (trailerIndex) => {
    let filteredTrailerOptions = [];
    const trailerDetailsAtIndex = this.state.trailerDetails[trailerIndex];
    const allTrailerOptions =
      trailerIndex > 0 ? this.trailerListWithoutStraightTruck : this.props.trailerList;
    if (this.isEmptyTrailer(trailerDetailsAtIndex.trailerStatus, trailerDetailsAtIndex.routeId)) {
      return allTrailerOptions;
    } else if (trailerDetailsAtIndex.routeName) {
      const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
      if (enableNewRoute) {
        const trailerIdsWithRoute = this.getTrailerIdsForUserCreatedRoute(
          trailerDetailsAtIndex.routeName
        );
        if (trailerIdsWithRoute.length > 0) {
          const filtered = allTrailerOptions.filter((t) =>
            trailerIdsWithRoute.some((id) => id?.toLowerCase() === t.value?.toLowerCase())
          );
          if (filtered.length > 0)
            return this.ensureSelectedTrailerInOptions({
              filteredOptions: filtered,
              selectedTrailer: trailerDetailsAtIndex,
              allOptions: allTrailerOptions
            });
        }
      }
      filteredTrailerOptions = allTrailerOptions.filter((trailer) =>
        isEquipmentRouteMatchingSelectedRoute({
          equipmentRouteName: trailer?.routeName,
          selectedRouteName: trailerDetailsAtIndex.routeName,
          routeList: this.props.routeList || [],
        })
      );
      if (enableNewRoute && filteredTrailerOptions.length > 0) {
        return this.ensureSelectedTrailerInOptions({
          filteredOptions: filteredTrailerOptions,
          selectedTrailer: trailerDetailsAtIndex,
          allOptions: allTrailerOptions
        });
      }
    }
    return filteredTrailerOptions?.length > 0 ? filteredTrailerOptions : allTrailerOptions;
  };

  // When user assigns a route to a trailer that didn't have it (e.g. DEFAULTROUTE → 014), the filtered-by-route list may exclude the selected trailer. Include it so the Trailer dropdown does not clear.
  ensureSelectedTrailerInOptions = ({ filteredOptions, selectedTrailer, allOptions }) => {
    if (!selectedTrailer?.trailerId) return filteredOptions;
    const alreadyInList = filteredOptions.some(
      (t) => t.value?.toLowerCase() === selectedTrailer.trailerId?.toLowerCase()
    );
    if (alreadyInList) return filteredOptions;
    const currentOption = allOptions.find(
      (t) => t.value?.toLowerCase() === selectedTrailer.trailerId?.toLowerCase()
    );
    return currentOption ? [currentOption, ...filteredOptions] : filteredOptions;
  };

  getFilteredRouteOptions = (routeIndex) => {
    let routeOptionsResult;
    let filteredRouteOptions = [];
    const trailerDetailsAtIndex = this.state.trailerDetails[routeIndex];
    const enableNewRoute = Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer);
    const isClosed = enableNewRoute && this.isClosedTrailer(trailerDetailsAtIndex.trailerStatus);
    const userCreatedRoutes = this.getUserCreatedRoutes(trailerDetailsAtIndex?.trailerId);
    const trailerRouteExcludingUserCreated = this.getTrailerRouteExcludingUserCreated(
      trailerDetailsAtIndex,
      userCreatedRoutes
    );
    if (this.isEmptyTrailer(trailerDetailsAtIndex.trailerStatus, trailerDetailsAtIndex.routeId)) {
      routeOptionsResult = ensureTrailerRouteInOptions({
        routeOptions: this.props.routeList,
        trailerRouteName: trailerRouteExcludingUserCreated
      });
    }
    // Closed trailer with no route (user cleared): show all available routes
    else if (isClosed && !trailerDetailsAtIndex?.routeName) {
      routeOptionsResult = ensureTrailerRouteInOptions({
        routeOptions: this.props.routeList,
        trailerRouteName: null
      });
    }
    // Closed with route OR non-closed: show trailer-associated filtered routes (from mapping + all trailers)
    else {
      if (trailerDetailsAtIndex?.routeName || trailerDetailsAtIndex?.trailerId) {
        const matchedEquipmentRoutes = this.props.trailerList
          .filter(
            (trailer) =>
              isEquipmentRouteMatchingSelectedRoute({
                equipmentRouteName: trailer.routeName,
                selectedRouteName: trailerDetailsAtIndex.routeName,
                routeList: this.props.routeList || [],
              }) || trailer.value === trailerDetailsAtIndex.trailerId
          )
          .map((trailer) => trailer.routeName)
          .filter(Boolean);
        const baseRouteList = getRouteOptionsIncludingFromAllTrailers({
          routeList: this.props.routeList,
          allTrailers: this.props.allTrailers
        });
        filteredRouteOptions = baseRouteList.filter(
          (route) =>
            matchedEquipmentRoutes.includes(route.mappedRouteName) ||
            matchedEquipmentRoutes.includes(route.value)
        );
      }
      const routeOptions = resolveBaseRouteOptions({
        filteredRouteOptions,
        trailer: trailerDetailsAtIndex,
        routeList: this.props.routeList,
        trailerList: this.props.trailerList
      });
      routeOptionsResult = ensureTrailerRouteInOptions({
        routeOptions,
        trailerRouteName: trailerRouteExcludingUserCreated
      });
    }
    return mergeUserCreatedRoutes(routeOptionsResult, userCreatedRoutes);
  };

  getIdealStartTimeError = (trailerIndex) => {
    if (!this.getSubmitIsPreDispatch() || !this.props.isStartTimeEditable) return null;
    const trailer = this.state.trailerDetails[trailerIndex];
    if (trailer?.isAlreadyDispatchedRoute) return null;
    const value = this.getEffectiveIdealStartTimeValue(trailerIndex);
    return value ? null : 'Required';
  };
  hasMissingIdealStartTime = () => {
    if (!this.getSubmitIsPreDispatch() || !this.props.isStartTimeEditable) return false;
    return this.state.trailerDetails.some(
      (trailer, i) => !trailer?.isAlreadyDispatchedRoute && !this.getEffectiveIdealStartTimeValue(i)
    );
  };

  render() {
    const { trailerDetails, trailerDetailsErrors } = this.state;
    const { dollyList, tractorList, isPreselectedDriver, maxAllowedTrailerCount } = this.props;

    const isStraightTruckSelected = this.props.isPreDispatch
      ? this.checkIfStraightTruck(trailerDetails)
      : this.checkIfStraightTruck(trailerDetails) || this.state.tractor?.isStraightTruck;

    const addTrailerCount = isStraightTruckSelected
      ? 0
      : maxAllowedTrailerCount - trailerDetails.length;
    const addTrailers = new Array(addTrailerCount).fill('addTrailers');

    const { classes } = this.props;
    return (
      <>
        <Grid className={`${classes.container} customScroll`}>
          <Header label="TRAILER DETAILS" />
          {trailerDetails.map((trailer, trailerIndex) => {
            const isRouteNameEditDisabled = this.props.getRouteNameDisableEdit?.(
              trailer,
              trailerIndex
            );
            return (
            <React.Fragment key={`${trailer.trailerId}-${trailerIndex}`}>
              {this.renderDeleteControl(trailerIndex, isRouteNameEditDisabled)}
              <DispatchInput
                label="Trailer #"
                autoFocus={isPreselectedDriver}
                value={trailer.trailerNumber ?? null}
                options={this.getFilteredTrailerOptions(trailerIndex)}
                onChange={this.updateTrailerNumber(trailerIndex)}
                error={trailerDetailsErrors?.[trailerIndex]?.trailerNumber}
                isLoading={this.state.isLoading}
                disableEdit={this.props.getRouteNameDisableEdit?.(trailer, trailerIndex)}
                isClearable
              />
              {Boolean(trailer?.defects?.length) && (
                <DefectDetails
                  defects={trailer?.defects}
                  equipmentType="trailers"
                  openDvirReports={this.props.openDvirReports(
                    EQUIPMENT_DEFECTS_GRID_FILTER.trailer
                  )}
                />
              )}
              <DispatchInput
                label="Manifest #"
                value={trailer.manifestNumber ?? null}
                disableEdit
              />
              {this.props.routeList.length > 0 ? (
                <DispatchInput
                  label="Route Name"
                  autoFocus={false}
                  value={getRouteNameDisplayValue({
                    routeName: trailer.routeName,
                    enableNewRouteAssignmentForTrailer: Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer),
                    routeOptions: this.getFilteredRouteOptions(trailerIndex),
                  })}
                  options={this.getFilteredRouteOptions(trailerIndex)}
                  onChange={this.updateRouteName(trailerIndex)}
                  isClearable
                  canAcceptNewValue={shouldShowAddRouteOption({
                    trailer,
                    enableNewRouteAssignmentForTrailer: this.props.configJSON?.enableNewRouteAssignmentForTrailer,
                    isClosedTrailer: this.isClosedTrailer(trailer.trailerStatus),
                  })}
                  createLabel={
                    shouldShowAddRouteOption({
                      trailer,
                      enableNewRouteAssignmentForTrailer: this.props.configJSON?.enableNewRouteAssignmentForTrailer,
                      isClosedTrailer: this.isClosedTrailer(trailer.trailerStatus),
                    })
                      ? "Route"
                      : undefined
                  }
                  allOptions={Boolean(this.props.configJSON?.enableNewRouteAssignmentForTrailer) ? getRouteOptionsForDuplicateCheck({
                    routeList: this.props.routeList,
                    allTrailers: this.props.allTrailers,
                    userCreatedRoutes: this.getUserCreatedRoutes(trailer?.trailerId),
                  }) : undefined}
                  format={ALPHA_NUMERIC_REGEX}
                  maxLength={this.props.routeNameConfig?.maxLength}
                  error={trailerDetailsErrors?.[trailerIndex]?.routeName}
                />
              ) : (
                <DispatchInput
                  label="Route Name"
                  value={trailer.routeName ?? null}
                  disableEdit
                  error={trailerDetailsErrors?.[trailerIndex]?.routeName}
                />
              )}
              {/* Dolly needed only for 2nd or more trailers are attached */}
              {trailerIndex > 0 ? (
                <>
                  <DispatchInput
                    label="Dolly #"
                    autoFocus={false}
                    value={trailer.dolly?.label ?? null}
                    options={dollyList}
                    onChange={this.updateDolly(trailerIndex)}
                    error={trailerDetailsErrors?.[trailerIndex]?.dolly}
                    disableEdit={trailer.isAlreadyDispatchedRoute}
                    isLoading={this.state.isLoading}
                    isClearable
                  />
                  {Boolean(trailer.dolly?.defects?.length) && (
                    <DefectDetails
                      defects={trailer.dolly?.defects}
                      equipmentType="dolly"
                      openDvirReports={this.props.openDvirReports(
                        EQUIPMENT_DEFECTS_GRID_FILTER.dolly
                      )}
                    />
                  )}
                </>
              ) : null}
              <Grid container className={classes.detailContainer}>
                <Detail
                  label="Bills"
                  value={trailer.numberOfProsLoaded}
                  inline
                />
              </Grid>
              <Grid container className={classes.detailContainer}>
                <Detail
                  label="Trailer Type"
                  value={trailer.trailerName ?? "__"}
                />
                <Detail
                  label="Trailer Status"
                  value={trailer.trailerStatus ?? "__"}
                />
                <PreDispatchIdealStartTimeEditor
                  isEditable={this.isIdealStartTimeEditable(trailer)}
                  value={this.props.preDispatchIdealStartTimes?.[trailerIndex] || trailer.idealStartTime || null}
                  displayValue={trailer.idealStartTime ?? trailer.plannedRouteStartTime ?? "__:__"}
                  error={!!this.getIdealStartTimeError?.(trailerIndex) || null}
                  helperText={this.getIdealStartTimeError?.(trailerIndex) || null}
                  onChange={this.props.updateIdealStartTime?.(trailerIndex) || null}
                />
              </Grid>
              {this.renderDivider(trailerIndex)}
            </React.Fragment>
            );
          })}

          {addTrailers.map((_newTrailer, trailerIndex) => (
            <AddTrailer key={trailerIndex} onClick={this.addNewTrailer} />
          ))}
          <Header label="TRACTOR DETAILS" />
          <DispatchInput
            label="Tractor #"
            autoFocus={false}
            value={this.state.tractor?.label || null}
            options={tractorList}
            onChange={this.onChangeTractor}
            isClearable
            error={this.state.tractorError}
            isLoading={this.state.isLoading}
          />
          {Boolean(this.state.tractor?.defects?.length) && (
            <DefectDetails
              defects={this.state.tractor.defects}
              equipmentType="tractors"
              openDvirReports={this.props.openDvirReports(EQUIPMENT_DEFECTS_GRID_FILTER.tractor)}
            />
          )}
          <Notes value={this.state.notes} onChange={this.updateNotes} />

          {this.props.configJSON?.enablePhysicalDrPrintPreview && (
            <PaperDeliveryReceiptSection
              trailerDetails={trailerDetails}
              isPrintPhysicalDrChecked={this.state.printDrAfterDispatch}
              onPrintCheckboxChange={this.handlePrintCheckboxChange}
            />
          )}

          {this.props.children}
        </Grid>
        <Action
          cancel={this.props.cancel}
          confirm={this.dispatchTrailer}
          isPreDispatch={this.props.isPreDispatch}
          submitLabel={getDispatchSubmitLabel(this.getSubmitIsPreDispatch())}
          disableButton={
            this.props.disableDispatch || this.state.isLoading || this.hasMissingIdealStartTime()
          }
        />
      </>
    );
  }
}

export default compose(withAppConfig, withStyles(styles))(DispatchTrailer);
